import {
  JsonPipe,
  NgClass,
  NgFor,
  NgIf,
  NgOptimizedImage,
} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  Product,
  ProductOption,
  ProductVariant,
} from 'src/app/classes/utility';
import { MercuryService } from 'src/app/service/mercury.service';
import { ProductService } from '../../../product.service';

@Component({
  selector: 'app-variant-picker',
  imports: [NgFor, NgClass, NgIf, RouterLink, NgOptimizedImage, JsonPipe],
  templateUrl: './variant-picker.component.html',
  styleUrl: './variant-picker.component.scss',
})
export class VariantPickerComponent implements OnChanges {
  @Input() product!: Product;
  @Input() relations: any;
  @Input() lines: any;
  @Output() selection = new EventEmitter();

  _iconRelations: any;

  _options: Partial<ProductOption | any>[] = [];
  _selectedVariant!: ProductVariant;
  _related: any;
  _relations: any;
  _sleeves: any;
  _selectedSleeve: any;
  _cupclass: string = '';
  _dropdownOpen: { [key: string]: boolean } = {};
  
  constructor(
    private productService: ProductService,
    private mercuryService: MercuryService
  ) {
    this.productService.variantObservable.subscribe((data) => {
      this._selectedVariant = data;
      console.log(data);
      console.log(this.product);
      this.ngOnChanges();
    });
  }

  selectSleeve(sleeve: any) {
    this._selectedSleeve = sleeve;
    this.productService.sleeveSubject.next({
      ...this._selectedSleeve.variants[0],
      images: this._selectedSleeve.images,
    });
  }

  clearSleeve() {
    this._selectedSleeve = undefined;
    this.productService.sleeveSubject.next(undefined);
    this._sleeves = [];
  }

  ngOnChanges(): void {
    if (!this.product) return;
    const title = this.product.title.toLocaleLowerCase();

    if (
      this.product.product_type.toLocaleLowerCase() == 'coffee cup' &&
      title.includes('bino')
    ) {
      this.updateSleeves(
        'premium-italian-leather-sleeve-bino-8oz-oat',
        'small'
      );
    } else if (
      (this.product.product_type.toLocaleLowerCase() == 'coffee cup' &&
        title.includes('camino')) ||
      title.includes('grande')
    ) {
      this.updateSleeves(
        'premium-italian-leather-sleeve-camino-grande-12oz-16oz-oat',
        'large'
      );
    } else {
      this.clearSleeve();
    }
    this.calculateOptions();
    this.buildRelated();

    this._related = this.productService.calculateOptions(
      this.relations,
      this.lines
    );
    if (this._related) {
      this._related.sort((a: any, b: any) => a.label.localeCompare(b.label));
    }

    if (this.relations?.length) {
      this._iconRelations = this.getLastElements(this.relations);
    }
  }

  private updateSleeves(handle: string, cupClass: string): void {
    if (this._cupclass !== cupClass) {
      this.clearSleeve();
    }
    this._cupclass = cupClass;

    this.mercuryService.getProductByHandle(handle).subscribe((data) => {
      const relatedHandles = (data?.metafields?.PRP?.related ?? [])
        .filter((x: any) => !x?.soldout)
        .map((x: any) => x?.handle);

      this.mercuryService
        .getProductsByHandles(relatedHandles)
        .subscribe((data) => {
          this._sleeves = data;
        });
    });
  }

  selectRelation(relation: any, option: any) {
    relation._active = true;
    this.selection.emit({ relation, option });
  }

  filterRelations(entry: any) {
    for (let entry of this._relations) {
      entry._active = false;
    }
    entry._active = true;
  }

  calculateOptions() {
    if (!this.product) return;

    this._options = structuredClone(this.product.options);

    this._options.forEach((option, optionIndex) => {
      option.value = option.values.find((value: string) =>
        this.activeOption(value, optionIndex)
      );

      option.values = option.values.map((value: string) => {
        const label = value;
        const available = this.calculateVariant(
          option,
          value,
          this._selectedVariant
        );
        return { value, label, available };
      });
    });
  }

  getLastElements(arr: any) {
    if (arr.length === 3) {
      return arr.slice(-1);
    } else if (arr.length === 2 || arr.length === 1) {
      return arr.slice(-1);
    } else {
      return [];
    }
  }

  calculateVariant(option: any, value: any, variant: any) {
    const options = {
      option1: option.position == 1 ? value : variant.option1,
      option2: option.position == 2 ? value : variant.option2,
      option3: option.position == 3 ? value : variant.option3,
    };
    const selectedVariant = this.product.variants.find((x: ProductVariant) => {
      return (
        x.option1 == options.option1 &&
        x.option2 == options.option2 &&
        x.option3 == options.option3
      );
    });
    return selectedVariant;
  }

  selectOption(option: ProductOption | any, value: string) {
    const selectedVariant = this.calculateVariant(
      option,
      value,
      this._selectedVariant
    );

    if (selectedVariant) {
      this.productService.variantSubject.next(selectedVariant);
      return selectedVariant;
    } else {
      return null;
    }
  }

  activeOption(value: string, i: number) {
    return (
      this._selectedVariant[('option' + (i + 1)) as keyof ProductVariant] ===
      value
    );
  }

  getVariantImage(option: any, value: string): string | null {
    const variant = this.calculateVariant(option, value, this._selectedVariant);
    if (variant && variant.image_id) {
      const image = this.product.images.find(img => img.id === variant.image_id);
      return image ? image.src : null;
    }
    return null;
  }

  toggleDropdown(optionName: string): void {
    this._dropdownOpen[optionName] = !this._dropdownOpen[optionName];
  }

  isDropdownOpen(optionName: string): boolean {
    return !!this._dropdownOpen[optionName];
  }

  selectDropdownOption(option: ProductOption | any, value: string): void {
    this.selectOption(option, value);
    this._dropdownOpen[option.name] = false;
  }

  buildRelated() {
    delete this._relations;
    let groups = [];
    try {
      if (this.product?.metafields?.PRP?.related) {
        let related = this.product.metafields.PRP.related;
        for (let entry of related) {
          if (entry.hidden) {
            continue;
          }

          const pos = groups.map((x) => x.group).indexOf(entry.group);
          if (pos == -1) {
            groups.push({
              group: entry.group,
              entries: [entry],
              defaultTitle: '',
            });
          } else {
            groups[pos].entries.push(entry);
          }
        }
        for (let group of groups) {
          for (let swatch of group.entries) {
            if (this.product.handle == swatch.handle) {
              group.defaultTitle = swatch.label;
            }
          }
        }

        this._relations = groups;
        const active = this._relations.find((x: any) =>
          x.entries.find((y: any) => y.id == this.product.id)
        );
        if (active) {
          active._active = true;
        }
      }
    } catch (err) {
      console.log(err);
    }
  }
}
